"use client";

import { useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
  type GridTone,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL, TABLE, TD, TH } from "@/components/tool-kit/styles";
import { SquareOptions } from "@/components/tools/ciphers-polygraphic/square-options";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  FOUR_SQUARE_SIZE,
  type FourSquareResult,
  type FourSquareStep,
  fourSquareTransform,
} from "@/lib/tool-kit/ciphers-polygraphic/four-square";
import type {
  GridAlphabet,
  SquareFill,
} from "@/lib/tool-kit/ciphers-polygraphic/squares";

const STEP_LIMIT = 30;

/** English strings. A translated content file overrides these through `ui`. */
export const FOUR_SQUARE_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "Help me Obi Wan Kenobi you are my only hope",
  keyword1: "Keyword for the upper-right square",
  keyword1Sample: "example",
  keyword2: "Keyword for the lower-left square",
  keyword2Sample: "keyword",
  keywordHint:
    "Its letters fill the square first, each used once; the rest of the alphabet follows in order. The two keywords may be the same, but then the cipher is weaker.",
  kind: "Fitting 26 letters into 25 cells",
  mergeIJ: "I and J share a cell",
  dropQ: "Leave out Q",
  fill: "Write the keyword into the square",
  fillHint:
    "Along the rows is the usual way. The American Cryptogram Association's example writes it down the columns.",
  fillRows: "Along the rows",
  fillColumns: "Down the columns",
  fillSpiral: "In a clockwise spiral",
  pairs: "Write the result in pairs",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  letterNote:
    "Only letters are enciphered. Spaces, digits and punctuation are dropped, and the result is in capitals.",
  padNote:
    "An odd last letter is padded with X (Z if the letter is X); decoded text keeps that filler.",
  ijNote: "I and J share a cell, so every J comes back as I.",
  droppedQ:
    "one: {count} Q was left out — this square has no Q.|other: {count} Qs were left out — this square has no Q.",
  unpaired:
    "The cipher text has an odd number of letters, so the last one ({letter}) was not decoded. Four-square cipher text always has an even length.",
  working: "The four squares and the pairs",
  squareUpperLeft: "Upper left — plain alphabet",
  squareUpperRight: "Upper right — keyed",
  squareLowerLeft: "Lower left — keyed",
  squareLowerRight: "Lower right — plain alphabet",
  squareHint:
    "Select a pair below. For encoding, its two plain letters (blue) sit in the two plain squares, and the cipher letters (amber) sit at the other corners of the rectangle they mark. Decoding runs the other way.",
  ruleEncode:
    "Encoding: find the first letter in the upper-left square and the second in the lower-right square. The first cipher letter is in the upper-right square, in the first letter's row and the second letter's column. The second cipher letter is in the lower-left square, in the second letter's row and the first letter's column.",
  ruleDecode:
    "Decoding: find the first cipher letter in the upper-right square and the second in the lower-left square. The first plain letter is in the upper-left square, in the first cipher letter's row and the second's column. The second plain letter is in the lower-right square, in the second cipher letter's row and the first's column.",
  pairColumn: "Pair",
  firstColumn: "First letter",
  secondColumn: "Second letter",
  resultColumn: "Result",
  place: "{letter}: row {row}, column {column}",
  showPair: "Show {pair} in the squares",
  filledMark: "padding added",
  stepLimit:
    "one: Showing the first pair.|other: Showing the first {count} pairs.",
} as const;

type Strings = typeof FOUR_SQUARE_CIPHER_STRINGS;

const TWO_LETTERS = /.{1,2}/g;

function pairUp(text: string, inPairs: boolean): string {
  return inPairs ? (text.match(TWO_LETTERS)?.join(" ") ?? "") : text;
}

/** Row and column to mark in each square for one step. */
function toneFor(
  step: FourSquareStep | undefined,
  square: "ul" | "ur" | "ll" | "lr"
): (index: number) => GridTone | undefined {
  return (index) => {
    if (!step) {
      return undefined;
    }
    const row = Math.floor(index / FOUR_SQUARE_SIZE);
    const column = index % FOUR_SQUARE_SIZE;
    const points = {
      ul: step.plainFirst,
      lr: step.plainSecond,
      ur: step.cipherFirst,
      ll: step.cipherSecond,
    };
    const point = points[square];
    if (point.row !== row || point.column !== column) {
      return undefined;
    }
    return square === "ul" || square === "lr" ? "primary" : "secondary";
  };
}

function Working({
  result,
  encoding,
  strings,
}: {
  result: FourSquareResult;
  encoding: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [selected, setSelected] = useState(0);
  const shown = result.steps.slice(0, STEP_LIMIT);
  const current = selected < shown.length ? selected : 0;
  const step = shown.at(current);
  const { squares } = result;
  const grids = [
    { key: "ul", cells: squares.upperLeft, label: strings.squareUpperLeft },
    { key: "ur", cells: squares.upperRight, label: strings.squareUpperRight },
    { key: "ll", cells: squares.lowerLeft, label: strings.squareLowerLeft },
    { key: "lr", cells: squares.lowerRight, label: strings.squareLowerRight },
  ] as const;
  const place = (letter: string, point: { row: number; column: number }) =>
    t(strings.place, {
      letter,
      row: fmt(point.row + 1),
      column: fmt(point.column + 1),
    });
  return (
    <>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {encoding ? strings.ruleEncode : strings.ruleDecode}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        {grids.map((grid) => (
          <div className="space-y-1" key={grid.key}>
            <p className={LABEL}>{grid.label}</p>
            <CipherGrid
              cells={[...grid.cells]}
              columns={FOUR_SQUARE_SIZE}
              label={grid.label}
              tone={toneFor(step, grid.key)}
            />
          </div>
        ))}
      </div>
      <p className={HINT}>{strings.squareHint}</p>
      {shown.length > 0 ? (
        <div className="space-y-2">
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className={TABLE}>
              <thead>
                <tr>
                  {[
                    strings.pairColumn,
                    strings.firstColumn,
                    strings.secondColumn,
                    strings.resultColumn,
                  ].map((label) => (
                    <th className={TH} key={label} scope="col">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((row, index) => {
                  const input = encoding ? row.plain : row.cipher;
                  return (
                    <tr
                      className={index === current ? "bg-primary/5" : undefined}
                      // biome-ignore lint/suspicious/noArrayIndexKey: pairs are positional
                      key={index}
                    >
                      <td className={`${TD} font-mono`}>
                        <button
                          aria-label={t(strings.showPair, { pair: input })}
                          aria-pressed={index === current}
                          className="rounded px-1 underline decoration-dotted underline-offset-4 hover:text-primary"
                          onClick={() => setSelected(index)}
                          type="button"
                        >
                          {input}
                        </button>
                        {row.filled && encoding ? (
                          <span className="ml-2 font-sans text-muted-foreground text-xs">
                            {strings.filledMark}
                          </span>
                        ) : null}
                      </td>
                      <td className={TD}>
                        {encoding
                          ? place(row.plain.charAt(0), row.plainFirst)
                          : place(row.cipher.charAt(0), row.cipherFirst)}
                      </td>
                      <td className={TD}>
                        {encoding
                          ? place(row.plain.charAt(1), row.plainSecond)
                          : place(row.cipher.charAt(1), row.cipherSecond)}
                      </td>
                      <td className={`${TD} font-mono`}>
                        {encoding ? row.cipher : row.plain}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {result.steps.length > STEP_LIMIT ? (
            <p className={HINT}>
              {tPlural(strings.stepLimit, locale, STEP_LIMIT, {
                count: fmt(STEP_LIMIT),
              })}
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export default function FourSquareCipherWidget() {
  const strings = useToolUi(FOUR_SQUARE_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyword1, setKeyword1] = useState<string>(strings.keyword1Sample);
  const [keyword2, setKeyword2] = useState<string>(strings.keyword2Sample);
  const [kind, setKind] = useState<GridAlphabet>("merge-ij");
  const [fill, setFill] = useState<SquareFill>("rows");
  const [inPairs, setInPairs] = useState(true);

  const encoding = mode === "encode";
  const result = fourSquareTransform(
    input,
    {
      keyword1,
      keyword2,
      kind: kind === "drop-q" ? "drop-q" : "merge-ij",
      fill,
    },
    mode
  );
  const output = pairUp(result.output, inPairs);

  const keyControls = (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.keywordHint}
          id="fs-keyword1"
          label={strings.keyword1}
          mono
          onChange={setKeyword1}
          value={keyword1}
        />
        <TextField
          id="fs-keyword2"
          label={strings.keyword2}
          mono
          onChange={setKeyword2}
          value={keyword2}
        />
      </div>
      <SquareOptions
        fill={fill}
        fillHint={strings.fillHint}
        fillLabel={strings.fill}
        fills={[
          { value: "rows", label: strings.fillRows },
          { value: "columns", label: strings.fillColumns },
          { value: "spiral", label: strings.fillSpiral },
        ]}
        idPrefix="fs"
        kind={kind}
        kindLabel={strings.kind}
        kinds={[
          { value: "merge-ij", label: strings.mergeIJ },
          { value: "drop-q", label: strings.dropQ },
        ]}
        onFillChange={setFill}
        onKindChange={setKind}
      />
      <Checkbox checked={inPairs} label={strings.pairs} onChange={setInPairs} />
    </div>
  );

  const notices = (
    <>
      {result.droppedQ > 0 ? (
        <Notice>
          {tPlural(strings.droppedQ, locale, result.droppedQ, {
            count: fmt(result.droppedQ),
          })}
        </Notice>
      ) : null}
      {result.unpaired ? (
        <ErrorNotice>
          {t(strings.unpaired, { letter: result.unpaired })}
        </ErrorNotice>
      ) : null}
    </>
  );

  return (
    <CipherWorkbench
      idPrefix="fs"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: encoding ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: encoding ? strings.cipherText : strings.decodedText,
        outputPlaceholder: strings.outputPlaceholder,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={notices}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      outputNotes={
        <div className="space-y-1">
          <p className={HINT}>{strings.letterNote}</p>
          <p className={HINT}>{strings.padNote}</p>
          {kind === "merge-ij" ? (
            <p className={HINT}>{strings.ijNote}</p>
          ) : null}
        </div>
      }
      working={
        <Working encoding={encoding} result={result} strings={strings} />
      }
    />
  );
}
